import React, { useState, useMemo, useRef, useEffect } from "react";
import "./classroom.css";
import { Icon } from "@iconify/react/dist/iconify.js";
import flatpickr from "flatpickr";
import { Spanish } from "flatpickr/dist/l10n/es";
import "flatpickr/dist/flatpickr.min.css";
import useClassroom from "../../hooks/useClassroom";
import ActividadFormModal from "./ActividadFormModal";
import ActividadDetailModal from "./ActividadDetailModal";
import EntregaModal from "./EntregaModal";
import RevisionModal from "./RevisionModal";
import DeleteConfirmModal from "./DeleteConfirmModal";
import Select2 from "../UI/Select2";
import {
  getMisEstudiantesService,
  getActividadByIdService,
} from "../../services/classroom/classroom.service";
import { toast } from "react-toastify";
import { toLocalDateString } from "../../utils/dateUtils";

/* ─── Constantes ─── */
const ESTADO_CONFIG = {
  PENDIENTE: {
    label: "Pendiente",
    icon: "mdi:clock-outline",
    badgeClass: "classroom-badge-pendiente",
    color: "#d97706",
  },
  ENTREGADA: {
    label: "Entregada",
    icon: "mdi:check-circle-outline",
    badgeClass: "classroom-badge-entregada",
    color: "#16a34a",
  },
  VALIDADA: {
    label: "Validada",
    icon: "mdi:check-decagram",
    badgeClass: "classroom-badge-validada",
    color: "#059669",
  },
  VENCIDA: {
    label: "Vencida",
    icon: "mdi:alert-circle-outline",
    badgeClass: "classroom-badge-vencida",
    color: "#dc2626",
  },
};

const ORDERING_OPTIONS = [
  { value: "-created_at", label: "Más recientes" },
  { value: "created_at", label: "Más antiguas" },
  { value: "fecha_entrega", label: "Fecha de entrega ↑" },
  { value: "-fecha_entrega", label: "Fecha de entrega ↓" },
  { value: "titulo", label: "Título A-Z" },
  { value: "-titulo", label: "Título Z-A" },
  { value: "estado", label: "Estado A-Z" },
  { value: "-estado", label: "Estado Z-A" },
];

const ActividadesLayer = () => {
  const {
    actividades,
    totalCount,
    loading,
    search,
    ordering,
    alumnoId,
    estado,
    revision,
    desde,
    hasta,
    canManage,
    canSubmit,
    canReview,
    isMentor,
    createActividad,
    updateActividad,
    deleteActividad,
    entregarActividad,
    revisarActividad,
    deleteArchivo,
    setSearch,
    setOrdering,
    setAlumnoId,
    setEstado,
    setRevision,
    setDesde,
    setHasta,
  } = useClassroom();

  // ─── Estado de modales ───
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEntregaModal, setShowEntregaModal] = useState(false);
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [selectedActividad, setSelectedActividad] = useState(null);
  const [detailActividad, setDetailActividad] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deletingArchivoId, setDeletingArchivoId] = useState(null);

  // ─── Estado de filtros locales ───
  const [searchInput, setSearchInput] = useState(search);
  const [desdeInput, setDesdeInput] = useState(desde);
  const [hastaInput, setHastaInput] = useState(hasta);

  // ─── Refs para flatpickr ───
  const desdeRef = useRef(null);
  const hastaRef = useRef(null);
  const desdePickerRef = useRef(null);
  const hastaPickerRef = useRef(null);

  // ─── Inicializar flatpickr ───
  useEffect(() => {
    if (desdeRef.current) {
      desdePickerRef.current = flatpickr(desdeRef.current, {
        dateFormat: "Y-m-d",
        altInput: true,
        altFormat: "d/m/Y",
        locale: Spanish,
        defaultDate: desde || undefined,
        onChange: ([date]) => {
          if (date) {
            const iso = toLocalDateString(date);
            setDesdeInput(iso);
            setDesde(iso);
          } else {
            setDesdeInput("");
            setDesde("");
          }
        },
      });
    }

    if (hastaRef.current) {
      hastaPickerRef.current = flatpickr(hastaRef.current, {
        dateFormat: "Y-m-d",
        altInput: true,
        altFormat: "d/m/Y",
        locale: Spanish,
        defaultDate: hasta || undefined,
        onChange: ([date]) => {
          if (date) {
            const iso = toLocalDateString(date);
            setHastaInput(iso);
            setHasta(iso);
          } else {
            setHastaInput("");
            setHasta("");
          }
        },
      });
    }

    return () => {
      desdePickerRef.current?.destroy();
      hastaPickerRef.current?.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Contadores rápidos ───
  const counts = useMemo(() => {
    const pendientes = actividades.filter((a) => a.estado === "PENDIENTE").length;
    const entregadas = actividades.filter((a) => a.estado === "ENTREGADA").length;
    const validadas = actividades.filter((a) => a.estado === "VALIDADA").length;
    const vencidas = actividades.filter((a) => a.estado === "VENCIDA").length;
    return { pendientes, entregadas, validadas, vencidas, total: totalCount };
  }, [actividades, totalCount]);

  // ─── fetchOptions para Select2 de alumnos ───
  const fetchAlumnos = async (searchTerm) => {
    const response = await getMisEstudiantesService(searchTerm);
    const data = Array.isArray(response) ? response : response?.results ?? [];
    return data.map((est) => ({
      id: est.estudiante_id ?? est.id,
      label: est.estudiante_nombre ?? est.nombre_completo ?? est.nombre ?? est.email ?? `Estudiante #${est.estudiante_id ?? est.id}`,
    }));
  };

  // ─── Handlers de filtros ───
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const handleEstadoClick = (value) => {
    setEstado(estado === value ? "" : value);
  };

  const handleAlumnoChange = (id) => {
    setAlumnoId(id || "");
  };

  const handleClearFilters = () => {
    setSearchInput("");
    setSearch("");
    setAlumnoId("");
    setEstado("");
    setRevision("");
    setDesdeInput("");
    setDesde("");
    setHastaInput("");
    setHasta("");
    desdePickerRef.current?.clear();
    hastaPickerRef.current?.clear();
  };

  const hasFilters = search || alumnoId || estado || revision || desde || hasta;

  const handleCreateSubmit = async (payload) => {
    setSubmitting(true);
    try {
      await createActividad(payload);
      setShowCreateModal(false);
    } catch {
      // Error ya mostrado por el hook
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSubmit = async (payload) => {
    if (!selectedActividad) return;
    setSubmitting(true);
    try {
      await updateActividad(selectedActividad.id, payload);
      setShowEditModal(false);
      setSelectedActividad(null);
    } catch {
      // Error ya mostrado por el hook
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedActividad) return;
    setSubmitting(true);
    try {
      await deleteActividad(selectedActividad.id);
      setShowDeleteModal(false);
      setShowDetailModal(false);
      setSelectedActividad(null);
    } catch {
      // Error ya mostrado por el hook
    } finally {
      setSubmitting(false);
    }
  };

  const handleEntregaSubmit = async (formData) => {
    if (!selectedActividad) return;
    setSubmitting(true);
    try {
      await entregarActividad(selectedActividad.id, formData);
      setShowEntregaModal(false);
      setShowDetailModal(false);
      setSelectedActividad(null);
    } catch {
      // Error ya mostrado por el hook
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevisionSubmit = async (payload) => {
    if (!selectedActividad) return;
    setSubmitting(true);
    try {
      await revisarActividad(selectedActividad.id, payload);
      setShowRevisionModal(false);
      setShowDetailModal(false);
      setSelectedActividad(null);
    } catch {
      // Error ya mostrado por el hook
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteArchivo = async (archivoId) => {
    if (!selectedActividad) return;
    setDeletingArchivoId(archivoId);
    try {
      await deleteArchivo(selectedActividad.id, archivoId);
      // Recargar el detalle para reflejar archivos actualizados
      const full = await getActividadByIdService(selectedActividad.id);
      setDetailActividad(full);
      setSelectedActividad(full);
    } catch {
      // Error ya mostrado por el hook
    } finally {
      setDeletingArchivoId(null);
    }
  };

  const openDetail = async (actividad) => {
    setSelectedActividad(actividad);
    setShowDetailModal(true);
    setLoadingDetail(true);
    try {
      const full = await getActividadByIdService(actividad.id);
      setDetailActividad(full);
    } catch {
      toast.error("Error al cargar el detalle de la actividad");
      setDetailActividad(actividad); // fallback con lo que tengamos del listado
    } finally {
      setLoadingDetail(false);
    }
  };

  const closeDetail = () => {
    setShowDetailModal(false);
    setSelectedActividad(null);
    setDetailActividad(null);
  };

  const openEditFromDetail = () => {
    setShowDetailModal(false);
    setShowEditModal(true);
  };

  const openDeleteFromDetail = () => {
    setShowDetailModal(false);
    setShowDeleteModal(true);
  };

  const openEntregaFromDetail = () => {
    setShowDetailModal(false);
    setShowEntregaModal(true);
  };

  const openRevisionFromDetail = () => {
    setShowDetailModal(false);
    setShowRevisionModal(true);
  };

  // ─── Helpers de fecha ───
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("es-MX", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("es-MX", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const isNearDeadline = (fechaEntrega) => {
    if (!fechaEntrega) return false;
    const now = new Date();
    const deadline = new Date(fechaEntrega);
    const diff = deadline - now;
    return diff > 0 && diff < 24 * 60 * 60 * 1000; // menos de 24h
  };

  const isPastDeadline = (fechaEntrega) => {
    if (!fechaEntrega) return false;
    return new Date(fechaEntrega) < new Date();
  };

  // ─── Render ───
  return (
    <div className="classroom-container">
     
      <div className="card border-0 shadow-sm mb-24">
        <div className="card-body p-16">
          {/* Fila 1: pills de estado + revision + botón crear */}
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-12 mb-16">
            <div className="d-flex align-items-center gap-8 flex-wrap">
              <button
                type="button"
                className={`btn btn-sm radius-20 px-14 py-6 fw-medium ${!estado && !revision ? "btn-primary" : "btn-outline-secondary"}`}
                onClick={() => { setEstado(""); setRevision(""); }}
              >
                Todas
              </button>
              <button
                type="button"
                className={`btn btn-sm radius-20 px-14 py-6 fw-medium d-flex align-items-center gap-4 ${estado === "PENDIENTE" ? "btn-warning text-white" : "btn-outline-warning"}`}
                onClick={() => { handleEstadoClick("PENDIENTE"); setRevision(""); }}
              >
                <Icon icon="mdi:clock-outline" className="text-sm" />
                Pendientes
              </button>
              <button
                type="button"
                className={`btn btn-sm radius-20 px-14 py-6 fw-medium d-flex align-items-center gap-4 ${estado === "ENTREGADA" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => { handleEstadoClick("ENTREGADA"); setRevision(""); }}
              >
                <Icon icon="mdi:file-upload-outline" className="text-sm" />
                Entregadas
              </button>
              <button
                type="button"
                className={`btn btn-sm radius-20 px-14 py-6 fw-medium d-flex align-items-center gap-4 ${estado === "VALIDADA" ? "btn-success" : "btn-outline-success"}`}
                onClick={() => { handleEstadoClick("VALIDADA"); setRevision(""); }}
              >
                <Icon icon="mdi:check-decagram" className="text-sm" />
                Validadas
              </button>
              <button
                type="button"
                className={`btn btn-sm radius-20 px-14 py-6 fw-medium d-flex align-items-center gap-4 ${estado === "VENCIDA" ? "btn-danger" : "btn-outline-danger"}`}
                onClick={() => { handleEstadoClick("VENCIDA"); setRevision(""); }}
              >
                <Icon icon="mdi:alert-circle-outline" className="text-sm" />
                Vencidas
              </button>

              {/* Atajo revision (solo mentor) */}
              {canManage && (
                <div className="vr mx-2 d-none d-md-block" style={{ height: "28px" }} />
              )}
              {canManage && (
                <select
                  className="form-select form-select-sm radius-20"
                  value={revision}
                  onChange={(e) => { setRevision(e.target.value); setEstado(""); }}
                  style={{ maxWidth: "200px", borderColor: "#e2e8f0", fontWeight: 500 }}
                >
                  <option value="">Por revisar: todas</option>
                  <option value="pendientes">Pendientes de revisar</option>
                  <option value="revisadas">Ya revisadas</option>
                </select>
              )}
            </div>

            {canManage && (
              <button
                type="button"
                className="btn btn-primary d-flex align-items-center gap-8 radius-10 px-16 py-8"
                onClick={() => {
                  setSelectedActividad(null);
                  setShowCreateModal(true);
                }}
              >
                <Icon icon="mdi:plus" style={{ fontSize: "18px" }} />
                <span>Nueva actividad</span>
              </button>
            )}
          </div>

          {/* Fila 2: búsqueda + filtros avanzados */}
          <div className="d-flex flex-wrap align-items-center gap-12">
            {/* Search */}
            <form onSubmit={handleSearchSubmit} className="position-relative flex-grow-1" style={{ maxWidth: "280px" }}>
              <Icon
                icon="mdi:magnify"
                className="position-absolute"
                style={{ left: "14px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: "18px" }}
              />
              <input
                type="text"
                className="form-control classroom-search-input"
                placeholder="Buscar..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </form>

            {/* Filtro por alumno (solo mentor) */}
            {canManage && (
              <div style={{ minWidth: "220px" }}>
                <Select2
                  value={alumnoId}
                  onChange={handleAlumnoChange}
                  fetchOptions={fetchAlumnos}
                  placeholder="Filtrar por alumno..."
                />
              </div>
            )}

            {/* Rango de fechas */}
            <div className="d-flex align-items-center gap-8">
              <input
                ref={desdeRef}
                type="text"
                className="form-control form-control-sm radius-8"
                placeholder="Desde"
                style={{ maxWidth: "140px", borderColor: "#e2e8f0" }}
                readOnly
              />
              <span className="text-secondary-light text-sm">—</span>
              <input
                ref={hastaRef}
                type="text"
                className="form-control form-control-sm radius-8"
                placeholder="Hasta"
                style={{ maxWidth: "140px", borderColor: "#e2e8f0" }}
                readOnly
              />
            </div>

            {/* Ordenar */}
            <select
              className="form-select form-select-sm radius-8"
              value={ordering}
              onChange={(e) => setOrdering(e.target.value)}
              style={{ maxWidth: "160px", borderColor: "#e2e8f0" }}
            >
              {ORDERING_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            {/* Limpiar filtros */}
            {hasFilters && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-4 radius-8"
                onClick={handleClearFilters}
              >
                <Icon icon="mdi:filter-remove-outline" style={{ fontSize: "16px" }} />
                <span>Limpiar</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ─── Lista de actividades ─── */}
      {loading ? (
        <div className="text-center py-48">
          <div className="spinner-border text-primary" role="status" />
          <p className="mt-12 text-secondary-light">Cargando actividades...</p>
        </div>
      ) : actividades.length === 0 ? (
        <div className="classroom-empty-state">
          <Icon
            icon="mdi:clipboard-text-outline"
            style={{ fontSize: "56px", color: "#94a3b8" }}
          />
          <h5 className="mt-16 fw-semibold text-secondary-light">
            No hay actividades
          </h5>
          <p className="text-secondary-light mb-24 text-center" style={{ maxWidth: "400px" }}>
            {canManage
              ? "Crea tu primera actividad para asignar tareas a tus estudiantes."
              : "Todavía no tienes actividades asignadas."}
          </p>
          {canManage && (
            <button
              type="button"
              className="btn btn-primary d-flex align-items-center gap-8 radius-10 px-20 py-10"
              onClick={() => {
                setSelectedActividad(null);
                setShowCreateModal(true);
              }}
            >
              <Icon icon="mdi:plus" style={{ fontSize: "18px" }} />
              <span>Crear actividad</span>
            </button>
          )}
        </div>
      ) : (
        <div className="row g-16">
          {actividades.map((actividad) => {
            const estadoCfg = ESTADO_CONFIG[actividad.estado] || ESTADO_CONFIG.PENDIENTE;
            const nearDeadline =
              actividad.estado === "PENDIENTE" && isNearDeadline(actividad.fecha_entrega);
            const pastDeadline =
              actividad.estado === "PENDIENTE" && isPastDeadline(actividad.fecha_entrega);

            return (
              <div key={actividad.id} className="col-12 col-md-6 col-xl-4">
                <div
                  className="card classroom-activity-card border-0 shadow-sm h-100"
                  style={{ cursor: "pointer", borderRadius: "16px", overflow: "hidden" }}
                  onClick={() => openDetail(actividad)}
                >
                  {/* Barra superior de color según estado */}
                  <div style={{ height: "4px", backgroundColor: estadoCfg.color }} />

                  <div className="card-body p-20">
                    {/* Header */}
                    <div className="d-flex align-items-start justify-content-between mb-12">
                      <div
                        className="w-40-px h-40-px d-flex align-items-center justify-content-center radius-10 flex-shrink-0"
                        style={{ backgroundColor: `${estadoCfg.color}15` }}
                      >
                        <Icon
                          icon={estadoCfg.icon}
                          style={{ color: estadoCfg.color, fontSize: "20px" }}
                        />
                      </div>
                      <span
                        className={`text-xs fw-medium px-8 py-2 radius-pill d-inline-flex align-items-center gap-4 ${estadoCfg.badgeClass}`}
                      >
                        {estadoCfg.label}
                      </span>
                    </div>

                    {/* Título */}
                    <h6 className="fw-semibold mb-8 text-truncate-2" style={{ minHeight: "40px", lineHeight: "1.4" }}>
                      {actividad.titulo}
                    </h6>

                    {/* Fecha de entrega */}
                    <div className="d-flex align-items-center gap-6 mb-12">
                      <Icon
                        icon="mdi:calendar-month"
                        className="text-sm flex-shrink-0"
                        style={{ color: pastDeadline ? "#dc2626" : nearDeadline ? "#d97706" : "#64748b" }}
                      />
                      <span
                        className={`text-sm ${pastDeadline ? "classroom-deadline-warning" : nearDeadline ? "classroom-deadline-near" : "text-secondary-light"}`}
                      > Antes de:{" "}
                        {pastDeadline ? "Venció: " : nearDeadline ? "Entrega: " : ""}
                        {formatDateTime(actividad.fecha_entrega)}
                      </span>
                    </div>

                    {/* Alumno / Docente */}
                    <div className="d-flex align-items-center gap-12 text-secondary-light text-xs mb-12">
                      {actividad.alumno_nombre && (
                        <span className="d-flex align-items-center gap-4">
                          <Icon icon="mdi:account-school-outline" className="text-sm" />
                          {actividad.alumno_nombre}
                        </span>
                      )}
                      {actividad.docente_nombre && (
                        <span className="d-flex align-items-center gap-4">
                          <Icon icon="mdi:account-tie-outline" className="text-sm" />
                          {actividad.docente_nombre}
                        </span>
                      )}
                    </div>

                   
                  </div>

                  {/* Footer con acciones */}
                  <div className="card-footer bg-transparent border-0 px-20 pb-16 pt-0 d-flex justify-content-end gap-6">
                    {canManage && actividad.estado !== "VALIDADA" && (
                      <>
                        <button
                          type="button"
                          className="classroom-action-btn classroom-action-btn--primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedActividad(actividad);
                            setShowEditModal(true);
                          }}
                          title="Editar"
                        >
                          <Icon icon="mdi:pencil-outline" style={{ fontSize: "18px" }} />
                        </button>
                        <button
                          type="button"
                          className="classroom-action-btn classroom-action-btn--danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedActividad(actividad);
                            setShowDeleteModal(true);
                          }}
                          title="Eliminar"
                        >
                          <Icon icon="mdi:trash-can-outline" style={{ fontSize: "18px" }} />
                        </button>
                      </>
                    )}
                    {canSubmit && actividad.estado === "PENDIENTE" && (
                      <button
                        type="button"
                        className="btn btn-sm btn-primary d-flex align-items-center gap-4 radius-8 px-10 py-4 text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedActividad(actividad);
                          setShowEntregaModal(true);
                        }}
                      >
                        <Icon icon="mdi:cloud-upload-outline" style={{ fontSize: "14px" }} />
                        Entregar
                      </button>
                    )}
                    {canSubmit && actividad.estado === "ENTREGADA" && (
                      <span className="text-xs text-primary fw-medium d-flex align-items-center gap-4">
                        <Icon icon="mdi:clock-outline" style={{ fontSize: "14px" }} />
                        En revisión
                      </span>
                    )}
                    {canSubmit && actividad.estado === "VALIDADA" && (
                      <span className="text-xs text-success fw-medium d-flex align-items-center gap-4">
                        <Icon icon="mdi:check-decagram" style={{ fontSize: "14px" }} />
                        Validada
                      </span>
                    )}
                    {canReview && actividad.estado === "ENTREGADA" && (
                      <button
                        type="button"
                        className="btn btn-sm btn-success d-flex align-items-center gap-4 radius-8 px-10 py-4 text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedActividad(actividad);
                          setShowRevisionModal(true);
                        }}
                      >
                        <Icon icon="mdi:check-decagram-outline" style={{ fontSize: "14px" }} />
                        Revisar
                      </button>
                    )}
                    {canReview && actividad.estado === "PENDIENTE" && (
                      <span className="text-xs text-secondary fw-medium d-flex align-items-center gap-4">
                        <Icon icon="mdi:clock-outline" style={{ fontSize: "14px" }} />
                        Sin entrega
                      </span>
                    )}
                    {canReview && actividad.estado === "VALIDADA" && (
                      <span className="text-xs text-success fw-medium d-flex align-items-center gap-4">
                        <Icon icon="mdi:check-decagram" style={{ fontSize: "14px" }} />
                        Validada
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ─── Modales ─── */}
      <ActividadFormModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onSubmit={handleCreateSubmit}
        submitting={submitting}
        title="Nueva Actividad"
      />

      <ActividadFormModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setSelectedActividad(null);
        }}
        onSubmit={handleEditSubmit}
        submitting={submitting}
        eventData={selectedActividad}
        title="Editar Actividad"
      />

      <ActividadDetailModal
        show={showDetailModal}
        onHide={closeDetail}
        actividad={detailActividad || selectedActividad}
        loading={loadingDetail}
        canManage={canManage}
        canSubmit={canSubmit}
        canReview={canReview}
        onEdit={openEditFromDetail}
        onDelete={openDeleteFromDetail}
        onEntregar={openEntregaFromDetail}
        onRevisar={openRevisionFromDetail}
        onDeleteArchivo={handleDeleteArchivo}
        deletingArchivoId={deletingArchivoId}
        formatDate={formatDate}
        formatDateTime={formatDateTime}
      />

      <DeleteConfirmModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        submitting={submitting}
        itemName={selectedActividad?.titulo}
      />

      <EntregaModal
        show={showEntregaModal}
        onHide={() => setShowEntregaModal(false)}
        onSubmit={handleEntregaSubmit}
        submitting={submitting}
        actividad={selectedActividad}
      />

      <RevisionModal
        show={showRevisionModal}
        onHide={() => setShowRevisionModal(false)}
        onSubmit={handleRevisionSubmit}
        submitting={submitting}
        actividad={selectedActividad}
      />
    </div>
  );
};

export default ActividadesLayer;
